import React, { useState, useEffect } from "react";
import { ArrowLeft, CheckCircle, Circle, Shield, AlertTriangle, ArrowRight, FileAudio, Music, Layers, Scissors, UserCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Link } from "react-router-dom";

// Audit checklist items configuration
const auditItems = [
  {
    id: "bpm-key",
    title: "BPM & KEY SIGNATURE IDENTIFIED",
    description: "Tempo and musical key are documented and accurate for cue sheet requirements.",
    icon: Music,
  },
  {
    id: "wav-main",
    title: "HIGH-RESOLUTION .WAV MAIN MIX READY",
    description: "Full quality stereo mix at 48kHz/24-bit minimum for broadcast delivery.",
    icon: FileAudio,
  },
  {
    id: "underscore",
    title: "UNDERSCORE (DRUMLESS) MIX READY",
    description: "Dialogue-friendly version with percussion removed for scene flexibility.",
    icon: Scissors,
  },
  {
    id: "stems",
    title: "FULL STEMS BOUNCED AND ZIPPED",
    description: "Individual instrument groups exported and packaged for editorial re-mixing.",
    icon: Layers,
  },
  {
    id: "clearance",
    title: "ZERO UNCLEARED SAMPLES / 100% HUMAN AUTHORED",
    description: "No third-party samples, loops, or AI-generated elements. Full copyright control.",
    icon: UserCheck,
  },
];

// Individual Audit Item Component
const AuditItem = ({ item, isChecked, onToggle, index }) => {
  const Icon = item.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 + index * 0.08 }}
      onClick={onToggle}
      data-testid={`audit-item-${item.id}`}
      className={`group cursor-pointer bg-[rgb(26,28,30)] border rounded-xl p-5 sm:p-6 transition-all duration-300 ${
        isChecked 
          ? "border-[rgb(74,222,128)] shadow-[0_0_20px_rgba(74,222,128,0.1)]" 
          : "border-[rgba(255,255,255,0.08)] hover:border-[rgba(218,255,1,0.3)]"
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-300 ${
          isChecked 
            ? "bg-[rgb(74,222,128)]" 
            : "bg-[rgb(40,42,45)] group-hover:bg-[rgba(218,255,1,0.1)]"
        }`}>
          {isChecked ? (
            <CheckCircle size={18} className="text-[rgb(17,17,19)]" />
          ) : (
            <Circle size={18} className="text-[rgb(100,100,110)] group-hover:text-[rgb(218,255,1)]" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <Icon size={16} className={`flex-shrink-0 ${isChecked ? "text-[rgb(74,222,128)]" : "text-[rgb(218,255,1)]"}`} />
            <h3
              className={`text-sm sm:text-base font-semibold tracking-wide transition-colors ${
                isChecked ? "text-[rgb(74,222,128)]" : "text-white"
              }`}
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {item.title}
            </h3>
          </div>
          <p
            className="text-[rgb(161,161,170)] text-xs sm:text-sm leading-relaxed pl-7"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {item.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const MetadataAuditPage = () => {
  const [checkedItems, setCheckedItems] = useState({});
  const [allChecked, setAllChecked] = useState(false);

  // Check if all items are verified
  useEffect(() => {
    const allItemsChecked = auditItems.every(item => checkedItems[item.id]);
    setAllChecked(allItemsChecked);
  }, [checkedItems]);

  const handleToggle = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const progress = (checkedCount / auditItems.length) * 100;

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden" data-testid="metadata-audit-page">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10 sm:mb-12"
          style={{ fontFamily: "'Inter', sans-serif" }}
          data-testid="back-to-portal-link"
        >
          <ArrowLeft size={14} />
          BACK TO PORTAL
        </Link>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10 sm:mb-12"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] sm:text-xs tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <Shield size={12} className="mr-2" />
            PRE-FLIGHT COMPLIANCE
          </Badge>

          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
          >
            METADATA <span className="text-[rgb(218,255,1)]">SELF-AUDIT</span>
          </h1>
          <p
            className="text-[rgb(161,161,170)] text-sm sm:text-base max-w-lg mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Verify your asset is broadcast-ready before submission. 
            Complete all checkpoints to unlock the Ingestion Portal.
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-3">
            <span
              className="text-[rgb(161,161,170)] text-xs tracking-[0.15em]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              AUDIT PROGRESS
            </span>
            <span
              className={`text-xs font-semibold tracking-[0.1em] ${
                allChecked ? "text-[rgb(74,222,128)]" : "text-[rgb(218,255,1)]"
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {checkedCount} / {auditItems.length} VERIFIED
            </span>
          </div>
          <div className="h-2 bg-[rgb(40,42,45)] rounded-full overflow-hidden">
            <motion.div
              className={`h-full rounded-full transition-colors duration-500 ${
                allChecked 
                  ? "bg-gradient-to-r from-[rgb(74,222,128)] to-[rgb(34,197,94)]" 
                  : "bg-gradient-to-r from-[rgb(218,255,1)] to-[rgb(180,220,0)]"
              }`}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
        </motion.div>

        {/* Audit Checklist */}
        <div className="space-y-3 sm:space-y-4 mb-10">
          {auditItems.map((item, index) => (
            <AuditItem
              key={item.id}
              item={item}
              isChecked={checkedItems[item.id]}
              onToggle={() => handleToggle(item.id)}
              index={index}
            />
          ))}
        </div>

        <Separator className="bg-[rgb(63,63,63)] my-8" />

        {/* Result Section */}
        <AnimatePresence mode="wait">
          {allChecked ? (
            <motion.div
              key="verified"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="text-center"
              data-testid="asset-verified-section"
            >
              {/* Verified Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="inline-flex items-center gap-3 bg-[rgba(74,222,128,0.1)] border-2 border-[rgb(74,222,128)] rounded-2xl px-6 py-4 mb-6"
              >
                <div className="w-12 h-12 rounded-full bg-[rgb(74,222,128)] flex items-center justify-center">
                  <CheckCircle size={28} className="text-[rgb(17,17,19)]" />
                </div>
                <div className="text-left">
                  <p
                    className="text-[rgb(74,222,128)] text-xl sm:text-2xl font-bold tracking-wide"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    ASSET VERIFIED
                  </p>
                  <p
                    className="text-[rgb(161,161,170)] text-xs tracking-[0.1em]"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    ALL COMPLIANCE CHECKPOINTS PASSED
                  </p>
                </div>
              </motion.div>

              <p
                className="text-[rgb(161,161,170)] text-sm mb-6 max-w-md mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Your asset meets broadcast-ready standards. 
                Proceed to submit your catalog for A&R consideration.
              </p>

              <Link
                to="/ingestion"
                data-testid="proceed-to-ingestion-btn"
                className="inline-flex items-center gap-2 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-sm font-semibold tracking-[0.1em] px-8 py-4 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4)] transition-all duration-300"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                PROCEED TO INGESTION PORTAL
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          ) : (
            <motion.div
              key="incomplete"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center"
              data-testid="audit-incomplete-section"
            >
              <div className="inline-flex items-center gap-2 text-amber-500 mb-4">
                <AlertTriangle size={18} />
                <span
                  className="text-sm tracking-[0.1em]"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  AUDIT INCOMPLETE
                </span>
              </div>
              <p
                className="text-[rgb(161,161,170)] text-sm max-w-md mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Complete all {auditItems.length - checkedCount} remaining checkpoint{auditItems.length - checkedCount !== 1 ? 's' : ''} to unlock submission access.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center"
        >
          <p
            className="text-[rgb(100,100,110)] text-xs tracking-[0.1em]"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Questions about asset preparation?{" "}
            <a
              href="mailto:leaver@itzgreenmusic.com?subject=Metadata%20Audit%20Question"
              className="text-[rgb(218,255,1)] hover:underline"
            >
              Contact A&R
            </a>
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default MetadataAuditPage;
